/* eslint-disable no-param-reassign */
import { createAction, createReducer } from "@reduxjs/toolkit"
import { defineMessages } from "react-intl"
import { saveAs } from "file-saver"
import getCountryISO2 from "country-iso-3-to-2"
import httpClient from "./httpClient"

const RESPONSE_DETAILS_LIST = createAction("RESPONSE_DETAILS_LIST")
const INFO_MESSAGE = createAction("INFO_MESSAGE")
const CLEAR_DETAILS_LIST = createAction("CLEAR_DETAILS_LIST")
const INITIALISE_FORM = createAction("INITIALISE_FORM")

const messages = defineMessages({
  formNotFound: {
    id: "forms.not.found",
    defaultMessage:
      "The form is no longer available. You will receive a notification when the form is back online."
  }
})

function formValuesToBody(formValues) {
  return Object.keys(formValues).map((key) => ({
    key,
    value: formValues[key]
  }))
}

/**
 * Update user form status to the next status
 * @param url ressource path used as a PUT
 * @param id UserForm unique ID
 * @param formValues UserForm values
 * @param cancelToken Cancellation token for axios request
 * @returns {Promise<AxiosResponse<any>>}
 */
function nextStepForm(url, id, formValues, cancelToken) {
  const body = {
    userFormId: id,
    values: formValues && formValuesToBody(formValues)
  }
  return httpClient.put(url, body, { cancelToken })
}
function initialiseForm(values) {
  return (dispatch) => {
    dispatch(INITIALISE_FORM(values))
  }
}
function getAttribute(identifier) {
  return httpClient
    .get(`/api/contact/attributes/${identifier}`)
    .then((attribute) => {
      const { jumpIdentifier } = attribute.data
      return jumpIdentifier
    })
}
async function getAttributesChamps(userFromId) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const result = await httpClient
    .get(`/api/contact/attributes/user/${userFromId}`)
    .then(({ data }) => data)
  if (result) {
    return result
  }
  return null
}
async function initialiseElementsForm(elements, userFormId) {
  const attributeChamps = await getAttributesChamps(userFormId)
  if (!attributeChamps) {
    return null
  }
  const initialValues = await elements.reduce(async (acc, elem) => {
    const ac = await acc
    let jumpIdentifier
    if (!elem.jumpAttributeIds[0]) {
      return ac
    }
    if (elem.type === "currency_value") {
      jumpIdentifier = await getAttribute(elem.jumpAttributeIds[0])
      const currency = attributeChamps[jumpIdentifier]?.value.replace(
        /[\d., ]/g,
        ""
      )
      const amount = parseFloat(
        attributeChamps[jumpIdentifier]?.value.replace(",", ".")
      )
      ac[elem.id] = {
        amount,
        currency
      }
      return ac
    }
    if (elem.type === "country") {
      jumpIdentifier = await getAttribute(elem.jumpAttributeIds[0])
      if (jumpIdentifier.length > 2) {
        ac[elem.id] = getCountryISO2(attributeChamps[jumpIdentifier]?.value)
      } else {
        ac[elem.id] = attributeChamps[jumpIdentifier]?.value
      }
      return ac
    }
    if (elem.type !== "address") {
      jumpIdentifier = await getAttribute(elem.jumpAttributeIds[0])
      ac[elem.id] = attributeChamps[jumpIdentifier]?.value
      return ac
    }
    ac[elem.id] = {}
    await elem.jumpAttributeIds.map(async (attribute, index) => {
      jumpIdentifier = await getAttribute(attribute)
      if (attributeChamps[jumpIdentifier]) {
        if (index === 0) {
          ac[elem.id].address = attributeChamps[jumpIdentifier].value
        } else if (index === 1) {
          ac[elem.id].city = attributeChamps[jumpIdentifier].value
        } else if (index === 2) {
          ac[elem.id].postalCode = attributeChamps[jumpIdentifier].value
        } else {
          ac[elem.id].country = attributeChamps[jumpIdentifier].value
        }
      }
    })

    return ac
  }, {})

  return initialValues
}
function fetchFormById(formId) {
  return (dispatch) =>
    httpClient
      .get(`/api/forms/${formId}`)
      .then(async ({ data }) => {
        const { values } = data
        let formValues
        if (Object.keys(values).length > 0) {
          formValues = values
        } else {
          formValues =
            (await initialiseElementsForm(data.elements, data._id)) || {}
        }
        if (Object.keys(formValues).length > 0) {
          await dispatch(initialiseForm(formValues))
        }
        return data
      })
      .catch((err) => {
        if (err.statusCode === 404) {
          dispatch(INFO_MESSAGE(messages.formNotFound))
          return
        }
        throw err
      })
}

/**
 * Used by admin when selecting a Form to get user form details.
 * @param formID
 * @returns {function(*): Promise<AxiosResponse<any>>}
 */
function fetchUserFormResponse(formID, cancelToken) {
  return (dispatch) => {
    return httpClient
      .get(`/api/forms/responses/${formID}`, { cancelToken })
      .then(({ data }) => dispatch(RESPONSE_DETAILS_LIST({ formID, data })))
  }
}

function save(id, formValues, cancelToken) {
  return () => nextStepForm("/api/form/save", id, formValues, cancelToken)
}

function submit(id, formValues, cancelToken) {
  return () => nextStepForm("/api/form/submit", id, formValues, cancelToken)
}

function sign(id, cancelToken) {
  return () => nextStepForm("/api/form/sign", id, null, cancelToken)
}

function clearUserFormResponse() {
  return (dispatch) => {
    dispatch(CLEAR_DETAILS_LIST())
  }
}

/**
 * Download form as a PDF File
 *
 * @returns a promise with download link
 * @param userForm
 */
function download(userForm) {
  // here
  const { name, url } = userForm
  return () => {
    return httpClient.get(url).then(({ data }) => {
      const blob = `data:application/pdf;base64,${data}`
      const filename = `${name}.pdf`
      saveAs(blob, filename)
    })
  }
}

const initialState = {
  infoMessage: undefined,
  responseDetails: undefined,
  initialValuesUser: {}
}

const reducer = createReducer(initialState, {
  RESPONSE_DETAILS_LIST: (state, { payload }) => {
    state.responseDetails = {
      ...state.responseDetails,
      [payload.formID]: payload.data
    }
  },
  CLEAR_DETAILS_LIST: (state) => {
    state.responseDetails = initialState.responseDetails
  },
  INITIALISE_FORM: (state, { payload }) => {
    state.initialValuesUser = payload
  }
})

export default {
  initialState,
  actions: {
    fetchUserFormResponse,
    fetchFormById,
    download,
    save,
    submit,
    sign,
    clearUserFormResponse
  },
  reducer
}
